import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Globe, MapPin, Search, Star, Filter, Calendar, Users, Award, ChevronDown, X, Languages, GraduationCap } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icon for coaches
const coachIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Map bounds change handler component
function MapBoundsHandler({ onBoundsChange }) {
  const map = useMap();
  
  useEffect(() => {
    const handleMoveEnd = () => {
      const bounds = map.getBounds();
      onBoundsChange({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest()
      });
    };
    
    map.on('moveend', handleMoveEnd);
    handleMoveEnd(); // Initial bounds
    
    return () => {
      map.off('moveend', handleMoveEnd);
    };
  }, [map, onBoundsChange]);
  
  return null;
}

function CoachesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [coaches, setCoaches] = useState([]);
  const [mapCoaches, setMapCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  
  const [filters, setFilters] = useState({
    country: searchParams.get('country') || '',
    city: searchParams.get('city') || '',
    sport: searchParams.get('sport') || '',
    skill_level: searchParams.get('skill_level') || '',
    language: searchParams.get('language') || '',
    min_rating: searchParams.get('min_rating') || '',
    online_coaching: searchParams.get('online') || '',
    verified_only: searchParams.get('verified') === 'true'
  });
  
  const [mapCenter, setMapCenter] = useState([20, 0]);
  const [mapZoom, setMapZoom] = useState(2);
  
  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  useEffect(() => {
    fetchCoaches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);
  
  const fetchInitialData = async () => {
    try {
      const countriesRes = await axios.get(`${API}/synergies/coaches/locations/countries`);
      setCountries(countriesRes.data);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
  };
  
  const fetchCoaches = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await axios.get(`${API}/synergies/coaches?${params.toString()}`);
      setCoaches(response.data.coaches);
    } catch (error) {
      console.error('Error fetching coaches:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchCities = async (country) => {
    if (!country) {
      setCities([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/synergies/coaches/locations/cities/${country}`);
      setCities(response.data);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };
  
  const handleMapBoundsChange = async (bounds) => {
    try {
      const response = await axios.get(`${API}/synergies/coaches/map/bounds`, {
        params: { ...bounds, sport: filters.sport || undefined }
      });
      setMapCoaches(response.data);
    } catch (error) {
      console.error('Error fetching map coaches:', error);
    }
  };
  
  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    if (key === 'country') {
      fetchCities(value);
      newFilters.city = '';
    }
    
    // Update URL
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    setSearchParams(params);
  };
  
  const clearFilters = () => {
    setFilters({
      country: '',
      city: '',
      sport: '',
      skill_level: '',
      language: '',
      min_rating: '',
      online_coaching: '',
      verified_only: false
    });
    setSearchParams(new URLSearchParams());
  };
  
  return (
    <div className="min-h-screen bg-deep-dark" data-testid="coaches-page">
      {/* Header */}
      <div className="bg-dark-surface border-b border-premium-gold/30/50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-1 bg-premium-gold/20 text-premium-gold rounded-full text-sm font-semibold tracking-wider mb-4">
                <Globe className="w-4 h-4" strokeWidth={1.5} />
                DIOS Synergies
              </span>
              <h1 
                className="text-4xl md:text-5xl font-bold tracking-tight text-white"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                Travel <span className="gold-gradient">Coaches</span>
              </h1>
              <p className="text-white/60 mt-2">Find expert coaches anywhere in the world</p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters Toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="mb-6 flex items-center gap-2 px-4 py-2 bg-dark-surface border border-premium-gold/30/50 rounded-lg text-white hover:border-premium-gold/50 transition-colors"
        >
          <Filter className="w-5 h-5 text-premium-gold" />
          <span className="font-semibold">Filters</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          {Object.values(filters).filter(v => v).length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-premium-gold text-white text-xs rounded-full">
              {Object.values(filters).filter(v => v).length}
            </span>
          )}
        </button>
        
        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-dark-surface p-6 rounded-xl border border-premium-gold/30/50 mb-8" data-testid="filters-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-white uppercase">Search Filters</h3>
              <button onClick={clearFilters} className="text-sm text-premium-gold hover:text-premium-gold-light flex items-center gap-1">
                <X className="w-4 h-4" /> Clear All
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">Country</label>
                <select
                  value={filters.country}
                  onChange={(e) => handleFilterChange('country', e.target.value)}
                  className="w-full px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:outline-none"
                >
                  <option value="">All Countries</option>
                  {countries.map(c => (
                    <option key={c.country} value={c.country}>{c.country} ({c.count})</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">City</label>
                <select
                  value={filters.city}
                  onChange={(e) => handleFilterChange('city', e.target.value)}
                  className="w-full px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:outline-none"
                  disabled={!filters.country}
                >
                  <option value="">All Cities</option>
                  {cities.map(c => (
                    <option key={c.city} value={c.city}>{c.city} ({c.count})</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">Sport</label>
                <input
                  type="text"
                  value={filters.sport}
                  onChange={(e) => handleFilterChange('sport', e.target.value)}
                  placeholder="e.g. cricket, tennis"
                  className="w-full px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white placeholder-white/40 focus:border-premium-gold focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">Skill Level</label>
                <select
                  value={filters.skill_level}
                  onChange={(e) => handleFilterChange('skill_level', e.target.value)}
                  className="w-full px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:outline-none"
                >
                  <option value="">All Levels</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                  <option value="professional">Professional</option>
                  <option value="olympic">Olympic</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">Language</label>
                <input
                  type="text"
                  value={filters.language}
                  onChange={(e) => handleFilterChange('language', e.target.value)}
                  placeholder="e.g. English, Spanish"
                  className="w-full px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white placeholder-white/40 focus:border-premium-gold focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">Min Rating</label>
                <select
                  value={filters.min_rating}
                  onChange={(e) => handleFilterChange('min_rating', e.target.value)}
                  className="w-full px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:outline-none"
                >
                  <option value="">Any Rating</option>
                  <option value="4.5">4.5+ Stars</option>
                  <option value="4.0">4.0+ Stars</option>
                  <option value="3.5">3.5+ Stars</option>
                  <option value="3.0">3.0+ Stars</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">Coaching Type</label>
                <select
                  value={filters.online_coaching}
                  onChange={(e) => handleFilterChange('online_coaching', e.target.value)}
                  className="w-full px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:outline-none"
                >
                  <option value="">All Types</option>
                  <option value="true">Online Only</option>
                  <option value="false">In-Person Only</option>
                </select>
              </div>
              
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.verified_only}
                    onChange={(e) => handleFilterChange('verified_only', e.target.checked)}
                    className="w-5 h-5 rounded border-premium-gold/30 bg-deep-dark text-premium-gold focus:ring-premium-gold"
                  />
                  <span className="text-sm text-white">Verified Only</span>
                </label>
              </div>
            </div>
          </div>
        )}
        
        {/* Map */}
        <div className="bg-dark-surface rounded-xl border border-premium-gold/30/50 overflow-hidden mb-8 h-64 sm:h-80 md:h-96">
          <MapContainer 
            center={mapCenter} 
            zoom={mapZoom} 
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapBoundsHandler onBoundsChange={handleMapBoundsChange} />
            {mapCoaches.map(coach => (
              <Marker 
                key={coach.coach_id} 
                position={[coach.latitude, coach.longitude]}
                icon={coachIcon}
              >
                <Popup>
                  <div className="p-2 min-w-[200px]">
                    <h3 className="font-bold text-sm mb-1">{coach.name}</h3>
                    <p className="text-xs text-gray-600 mb-2">{coach.city}, {coach.country}</p>
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="w-4 h-4 text-yellow-500 fill-current" strokeWidth={1.5} />
                      <span className="text-sm font-semibold">{coach.rating?.toFixed(1) || 'New'}</span>
                    </div>
                    <p className="text-xs text-gray-600 mb-2">
                      {coach.specializations?.slice(0, 2).join(', ')}
                    </p>
                    <Link 
                      to={`/synergies/coaches/${coach.coach_id}`}
                      className="inline-block mt-2 px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                    >
                      View Profile
                    </Link>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
        
        {/* Results */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">
            {loading ? 'Loading...' : `${coaches.length} Coaches Found`}
          </h2>
        </div>
        
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-3 border-premium-gold/20 border-t-premium-gold"></div>
          </div>
        ) : coaches.length === 0 ? (
          <div className="text-center py-16 bg-dark-surface rounded-xl border border-premium-gold/30/50">
            <Award className="w-16 h-16 text-white/30 mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-xl text-white/60">No coaches found matching your criteria.</p>
            <button onClick={clearFilters} className="mt-4 text-premium-gold hover:text-premium-gold-light">
              Clear filters and try again
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coaches.map(coach => (
              <Link 
                key={coach.coach_id}
                to={`/synergies/coaches/${coach.coach_id}`}
                className="bg-dark-surface rounded-xl overflow-hidden border border-premium-gold/30/50 hover:border-premium-gold/30 transition-all duration-300 group"
                data-testid={`coach-card-${coach.coach_id}`}
              >
                <div className="h-48 bg-deep-dark overflow-hidden relative">
                  {coach.photo ? (
                    <img 
                      src={coach.photo} 
                      alt={coach.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-imperial to-dark-purple">
                      <Award className="w-16 h-16 text-white/20" strokeWidth={1.5} />
                    </div>
                  )}
                  {/* Badges */}
                  <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                    {coach.verified && (
                      <span className="px-2 py-1 bg-premium-gold text-dark-purple text-xs font-bold rounded">
                        Verified
                      </span>
                    )}
                    {coach.featured && (
                      <span className="px-2 py-1 bg-premium-gold text-white text-xs font-bold rounded">
                        Featured
                      </span>
                    )}
                  </div>
                  {/* Rating */}
                  {coach.rating > 0 && (
                    <div className="absolute top-4 right-4 flex items-center gap-1 bg-dark-surface/80 backdrop-blur px-2 py-1 rounded-full">
                      <Star className="w-4 h-4 text-premium-gold fill-current" strokeWidth={1.5} />
                      <span className="text-sm font-bold text-premium-gold">{coach.rating.toFixed(1)}</span>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white group-hover:text-premium-gold transition-colors mb-2" style={{ fontFamily: 'Fraunces, serif' }}>
                    {coach.name}
                  </h3>
                  <div className="flex items-center gap-2 text-white/60 text-sm mb-3">
                    <MapPin className="w-4 h-4" strokeWidth={1.5} />
                    <span>{coach.city}, {coach.country}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {coach.specializations?.slice(0, 3).map((spec, idx) => (
                      <span key={idx} className="px-2 py-1 bg-deep-dark text-white/70 text-xs rounded capitalize">
                        {spec}
                      </span>
                    ))}
                  </div>
                  {coach.languages?.length > 0 && (
                    <div className="flex items-center gap-2 text-xs text-white/50 mb-4">
                      <Languages className="w-4 h-4" />
                      <span>{coach.languages.slice(0, 3).join(', ')}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-4 border-t border-premium-gold/30">
                    <div className="flex items-center gap-1 text-white/50 text-sm">
                      <GraduationCap className="w-4 h-4" />
                      <span>{coach.experience_years}yrs exp</span>
                    </div>
                    <button className="px-4 py-2 border border-premium-gold text-premium-gold hover:bg-premium-gold hover:text-dark-purple font-semibold text-sm rounded transition-all">
                      View Profile
                    </button>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CoachesPage;
